import type { RuntimeAppConfig } from './runtimeConfig';

// The four deployment labels. `es`/`uk` are markets (each panel reaches only its own
// market's app-service); `sandbox`/`dev` are non-production environments.
export const MARKET_LABELS = {
  es: 'ES',
  uk: 'UK',
  sandbox: 'Sandbox',
  dev: 'Dev',
} as const;

export type Market = keyof typeof MARKET_LABELS;

// The indicator always renders. When MARKET is missing, outside the known set, or a
// market (es/uk) whose configured API host contradicts it, it degrades to this sentinel
// so a broken deployment shows a visible warning instead of a confident-but-wrong market.
export const FALLBACK_TEXT = 'Soporte —';

export interface MarketIndicator {
  text: string; // e.g. "Soporte ES" | "Soporte —"
  degraded: boolean; // true → warning styling (fail-loud)
}

function isMarket(value: string | undefined): value is Market {
  return value === 'es' || value === 'uk' || value === 'sandbox' || value === 'dev';
}

// es must resolve to the ES app-service (api.citibox.com); uk to api.uk.citibox.com.
// Keyed off the `uk` subdomain marker. An empty or unparseable base counts as a
// mismatch — if the host cannot be verified, do not show a confident market label.
function hostMatchesMarket(market: 'es' | 'uk', apiBaseUrl: string | undefined): boolean {
  if (apiBaseUrl === undefined || apiBaseUrl === '') return false;
  let host: string;
  try {
    host = new URL(apiBaseUrl).hostname.toLowerCase();
  } catch {
    return false;
  }
  const hostIsUk =
    host === 'uk.citibox.com' || host.endsWith('.uk.citibox.com') || host.startsWith('uk.');
  return market === 'uk' ? hostIsUk : !hostIsUk;
}

// The market this deployment actually serves, or null when the runtime config cannot be
// trusted to name it: absent, outside the known set, or — for a real market — contradicted by
// the API host it is pointed at. Single source for every consumer that must not guess a market.
export function resolveMarket(config: RuntimeAppConfig): Market | null {
  const market = config.MARKET;
  if (!isMarket(market)) return null;
  if ((market === 'es' || market === 'uk') && !hostMatchesMarket(market, config.API_BASE_URL)) {
    return null;
  }
  return market;
}

export function resolveMarketIndicator(config: RuntimeAppConfig): MarketIndicator {
  const market = resolveMarket(config);
  if (market === null) return { text: FALLBACK_TEXT, degraded: true };
  return { text: `Soporte ${MARKET_LABELS[market]}`, degraded: false };
}

// Set once at boot: the market never changes at runtime, and a short label ("Soporte UK")
// lets an agent pick the right tab among several open panels without opening each one.
export function applyDocumentTitle(config: RuntimeAppConfig): string {
  const { text } = resolveMarketIndicator(config);
  document.title = text;
  return text;
}
